// src/components/ProviderTypeahead.js
//
// A lightweight searchable provider picker. The user types part of a provider
// name (or city) and matching providers appear in a dropdown to choose from —
// a replacement for a long fixed <select>. No external dependencies.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, ListGroup } from "react-bootstrap";

function providerLabel(p) {
  return (
    p.name +
    (p.city ? ` — ${p.city}` : "") +
    (p.phone ? ` (${p.phone})` : "")
  );
}

const MAX_RESULTS = 50;

export default function ProviderTypeahead({
  providers = [],
  value,            // selected provider_id as a string ("" = none)
  onChange,         // (idString) => void
  disabled = false,
  placeholder = "Start typing a provider name…",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const selected = value
    ? providers.find((p) => String(p.id) === String(value))
    : null;
  const selectedName = selected ? selected.name : "";

  // Keep the latest selected name available to the (once-registered) outside
  // click handler without re-binding it on every render.
  const selectedNameRef = useRef(selectedName);
  selectedNameRef.current = selectedName;

  // Sync the input text to the committed selection whenever it changes
  // externally — e.g. edit mode loads a ride, the providers list finishes
  // loading, or a brand-new provider is added inline. Typing does NOT change
  // `value`/`providers`, so in-progress text is never clobbered here.
  useEffect(() => {
    setQuery(selectedName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, providers]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return providers.slice(0, MAX_RESULTS);
    return providers
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.city && p.city.toLowerCase().includes(q))
      )
      .slice(0, MAX_RESULTS);
  }, [query, providers]);

  // Close (and discard uncommitted typing) on a click outside the widget.
  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(selectedNameRef.current);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const choose = (p) => {
    onChange(p ? String(p.id) : "");
    setQuery(p ? p.name : "");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      setHighlight(0);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && matches[highlight]) {
        e.preventDefault();
        choose(matches[highlight]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery(selectedNameRef.current);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <Form.Control
        type="text"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && !disabled && (
        <ListGroup
          style={{
            position: "absolute",
            zIndex: 1056, // above the Bootstrap modal (1055)
            width: "100%",
            maxHeight: "240px",
            overflowY: "auto",
            boxShadow: "0 0.5rem 1rem rgba(0,0,0,.175)",
          }}
        >
          <ListGroup.Item
            action
            active={!value}
            onMouseEnter={() => setHighlight(-1)}
            onMouseDown={(e) => {
              e.preventDefault();
              choose(null);
            }}
          >
            — No Provider —
          </ListGroup.Item>
          {matches.map((p, i) => (
            <ListGroup.Item
              key={p.id}
              action
              active={i === highlight}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(p);
              }}
            >
              {providerLabel(p)}
            </ListGroup.Item>
          ))}
          {matches.length === 0 && (
            <ListGroup.Item disabled>No matching providers</ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
}
